import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Globe, ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import useEmblaCarousel from 'embla-carousel-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');

  useEffect(() => {
    fetchClient();
  }, [slug]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi]);

  const fetchClient = async () => {
    try {
      const response = await axios.get(`${API}/clients/${slug}`);
      setClient(response.data);
    } catch (error) {
      console.error('Error fetching client:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const openLightbox = (image) => {
    setLightboxImage(image);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
    document.body.style.overflow = 'unset';
  };

  // Handle ESC key to close lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && lightboxOpen) {
        closeLightbox();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto mb-4"></div>
          <p className="text-gray-400">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold mb-4">Client Not Found</h2>
          <Button onClick={() => navigate('/portfolio')} className="bg-[#F4B400] text-black hover:bg-[#d49f00]">
            Back to Portfolio
          </Button>
        </div>
      </div>
    );
  }

  const getSocialIcon = (platform) => {
    const icons = {
      Facebook: '📘',
      Instagram: '📷',
      TikTok: '🎵',
      YouTube: '🎥',
      LinkedIn: '💼',
      Twitter: '🐦'
    };
    return icons[platform] || '🔗';
  };

  return (
    <div className="bg-black text-white pt-24">
      {/* Section 1: Hero */}
      <section className="py-16 bg-gradient-to-br from-black to-gray-900 border-b border-gray-800">
        <div className="container mx-auto px-6">
          <Button
            onClick={() => navigate('/portfolio')}
            variant="outline"
            className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black mb-8"
          >
            <ArrowLeft className="mr-2" size={16} />
            Back to Portfolio
          </Button>

          <div className="max-w-4xl">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-5xl md:text-6xl font-bold mb-4">{client.clientName}</h1>
                <Badge className="bg-[#F4B400] text-black text-lg px-4 py-1">{client.year}</Badge>
              </div>
            </div>

            {/* External Links */}
            <div className="flex flex-wrap gap-4 mt-8">
              {client.website && (
                <a 
                  href={client.website} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg hover:bg-[#F4B400] transition-all duration-300"
                >
                  <Globe size={18} />
                  <span className="font-semibold">Visit Website</span>
                  <ExternalLink size={14} />
                </a>
              )}

              {client.socialLinks && client.socialLinks.map((link, index) => (
                <a 
                  key={index}
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-[#F4B400] hover:text-black transition-all duration-300"
                >
                  <span>{getSocialIcon(link.platform)}</span>
                  <span className="font-semibold">{link.platform}</span>
                  <ExternalLink size={14} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Overview with Cover Image */}
      <section className="py-16 bg-white text-black">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-center">Project Overview</h2>
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Text */}
              <div>
                <p className="text-lg text-gray-700 leading-relaxed">
                  {client.description}
                </p>
              </div>
              {/* Cover Image */}
              {client.coverImage && (
                <div 
                  className="relative group cursor-pointer"
                  onClick={() => openLightbox(client.coverImage)}
                >
                  <img 
                    src={client.coverImage} 
                    alt={`${client.clientName} cover`}
                    className="w-full h-auto rounded-lg shadow-2xl transition-transform duration-300 group-hover:scale-[1.02]"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                    <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={40} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Services Delivered */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-8 text-center">Services Delivered</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {client.servicesPerformed.map((service, index) => (
                <Badge 
                  key={index}
                  className="bg-white text-black hover:bg-[#F4B400] hover:text-black transition-all duration-300 px-4 py-2 text-base"
                >
                  {service}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Gallery */}
      {client.galleryImages && client.galleryImages.length > 0 && (
        <section className="py-16 bg-white text-black">
          <div className="container mx-auto px-6">
            <h2 className="text-4xl font-bold mb-12 text-center">Project Gallery</h2>
            
            {client.galleryImages.length === 1 ? (
              /* Single image - display normally with click to zoom */
              <div className="max-w-4xl mx-auto">
                <div 
                  className="relative group cursor-pointer"
                  onClick={() => openLightbox(client.galleryImages[0])}
                >
                  <img 
                    src={client.galleryImages[0]} 
                    alt={`${client.clientName} project`}
                    className="w-full rounded-lg shadow-2xl transition-transform duration-300 group-hover:scale-[1.01]"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                    <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={48} />
                  </div>
                </div>
              </div>
            ) : (
              /* Multiple images - display as carousel */
              <div className="max-w-6xl mx-auto relative">
                <div className="overflow-hidden" ref={emblaRef}>
                  <div className="flex">
                    {client.galleryImages.map((image, index) => (
                      <div key={index} className="flex-[0_0_100%] min-w-0 px-4">
                        <div 
                          className="relative group cursor-pointer"
                          onClick={() => openLightbox(image)}
                        >
                          <img 
                            src={image} 
                            alt={`${client.clientName} project ${index + 1}`}
                            className="w-full h-[600px] object-cover rounded-lg shadow-2xl transition-transform duration-300 group-hover:scale-[1.01]"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 rounded-lg flex items-center justify-center">
                            <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" size={48} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                {/* Navigation Buttons */}
                <Button
                  onClick={scrollPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-black text-[#F4B400] border border-[#F4B400] hover:bg-[#F4B400] hover:text-black rounded-full w-12 h-12 p-0"
                  aria-label="Previous image"
                >
                  <ChevronLeft size={24} />
                </Button>
                <Button
                  onClick={scrollNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-black text-[#F4B400] border border-[#F4B400] hover:bg-[#F4B400] hover:text-black rounded-full w-12 h-12 p-0"
                  aria-label="Next image"
                >
                  <ChevronRight size={24} />
                </Button>
                
                {/* Dots indicator */}
                <div className="flex justify-center gap-2 mt-6">
                  {client.galleryImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => emblaApi && emblaApi.scrollTo(index)}
                      className={`h-2 rounded-full transition-all ${
                        index === selectedIndex 
                          ? 'bg-[#F4B400] w-8' 
                          : 'bg-gray-300 w-2'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-black to-gray-900">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Interested in Similar Results?</h2>
            <p className="text-gray-300 text-lg mb-8">
              Let's discuss how we can help transform your brand
            </p>
            <Button
              onClick={() => navigate('/contact')}
              size="lg"
              className="bg-[#F4B400] text-black hover:bg-[#d49f00] text-lg px-8 py-6"
            >
              Get in Touch
            </Button>
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white hover:text-[#F4B400] transition-colors z-10"
            aria-label="Close lightbox"
          >
            <X size={32} />
          </button>
          
          <img 
            src={lightboxImage} 
            alt="Full size view"
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gray-400 text-sm">
            Click outside or press ESC to close
          </p>
        </div>
      )}
    </div>
  );
};

export default ClientDetail;
